import type {
  GraphMember,
} from '@/contexts/FamilyGraphContext';

import {
  computeTreeBranchPaths,
} from '@/lib/treeBranchPathAuthority';

import type {
  CanonicalTreeRelationshipEdge,
} from './CanonicalTreeRelationshipEdge';

import {
  projectCanonicalTreeForViewer,
  type CanonicalTreeViewerProjectionResult,
  type CanonicalViewerBranch,
} from './CanonicalTreeViewerProjection';

export interface CanonicalTreeLayoutPathCompatibilityInput {
  readonly members:
    readonly GraphMember[];

  readonly viewerId:
    string | null | undefined;

  readonly edges:
    readonly CanonicalTreeRelationshipEdge[];
}

export interface CanonicalTreeLayoutPathDifference {
  readonly memberId: string;

  readonly classification:
    | 'PARITY'
    | 'CANONICAL-ADDITION'
    | 'LEGACY-ONLY'
    | 'PATH-MISMATCH';

  readonly legacyPath:
    string | null;

  readonly canonicalLayoutPath:
    string | null;
}

export interface CanonicalTreeLayoutPathCompatibilityResult {
  readonly viewerId:
    string | null;

  readonly layoutPathByMemberId:
    ReadonlyMap<string, string>;

  readonly legacyPathByMemberId:
    ReadonlyMap<string, string>;

  readonly projection:
    CanonicalTreeViewerProjectionResult | null;

  readonly differences:
    readonly CanonicalTreeLayoutPathDifference[];

  readonly parityCount: number;
  readonly canonicalAdditionCount: number;
  readonly legacyOnlyCount: number;
  readonly mismatchCount: number;

  readonly unresolvedEvidenceCount: number;
  readonly conflictingEvidenceCount: number;

  readonly readiness:
    | 'READY'
    | 'READY-WITH-CANONICAL-ADDITIONS'
    | 'BLOCKED';
}

const activeStructuralStatuses =
  new Set<
    CanonicalTreeRelationshipEdge['status']
  >([
    'active',
    'not-applicable',
  ]);

const isActiveParentEdge = (
  edge: CanonicalTreeRelationshipEdge,
): boolean =>
  edge.kind === 'parent-child' &&
  activeStructuralStatuses.has(edge.status) &&
  edge.confirmation !== 'rejected';

const isActivePartnerEdge = (
  edge: CanonicalTreeRelationshipEdge,
): boolean =>
  edge.kind === 'spouse-partner' &&
  edge.status === 'active' &&
  edge.rendersActivePartnership &&
  edge.confirmation !== 'rejected';

const branchRank = (
  branch:
    CanonicalViewerBranch | undefined,
): number => {
  switch (branch) {
    case 'maternal':
      return 0;

    case 'paternal':
      return 1;

    case 'both':
      return 2;

    case 'self':
      return 3;

    case 'descendant':
      return 4;

    case 'partner-affiliated':
      return 5;

    case 'child-mediated':
      return 6;

    case 'affiliated':
      return 7;

    case 'unresolved':
    default:
      return 8;
  }
};

const freezeMap = (
  values:
    ReadonlyMap<string, string>,
): ReadonlyMap<string, string> =>
  new Map(values);

const localParentRoleRank = (
  member:
    GraphMember | undefined,
): number => {
  /*
   * Preserve the exact legacy numeric layout grammar.
   *
   * Legacy branch paths rank a parent as maternal when either its
   * relationship is 'mother' or its inherited side is 'mother'.
   * The same rule applies to paternal ranking. Stable member ID is
   * therefore the tie-break when both parents inherit one branch side.
   */
  if (
    member?.relationship === 'mother' ||
    member?.side === 'mother'
  ) {
    return 0;
  }

  if (
    member?.relationship === 'father' ||
    member?.side === 'father'
  ) {
    return 1;
  }

  return 2;
};

const orderedParentIds = (
  ids:
    readonly string[],

  projection:
    CanonicalTreeViewerProjectionResult,

  memberById:
    ReadonlyMap<string, GraphMember>,
): string[] =>
  Array.from(
    new Set(ids),
  ).sort((left, right) => {
    /*
     * Preserve pyramidLayout's numeric compatibility grammar:
     * local mother-role parent first, local father-role parent second.
     *
     * Canonical viewer branch remains the secondary governed ordering
     * authority. Stable member ID is the final immutable tie-break.
     */
    const localRoleDelta =
      localParentRoleRank(
        memberById.get(left),
      ) -
      localParentRoleRank(
        memberById.get(right),
      );

    if (localRoleDelta !== 0) {
      return localRoleDelta;
    }

    const leftBranch =
      projection.projections.get(
        left,
      )?.branch;

    const rightBranch =
      projection.projections.get(
        right,
      )?.branch;

    const branchDelta =
      branchRank(leftBranch) -
      branchRank(rightBranch);

    if (branchDelta !== 0) {
      return branchDelta;
    }

    return left.localeCompare(right);
  });

const orderedStableMemberIds = (
  ids:
    readonly string[],
): string[] =>
  Array.from(
    new Set(ids),
  ).sort(
    (left, right) =>
      left.localeCompare(right),
  );

const buildCanonicalLayoutPaths = ({
  members,
  viewerId,
  edges,
  projection,
}: {
  readonly members:
    readonly GraphMember[];

  readonly viewerId: string;

  readonly edges:
    readonly CanonicalTreeRelationshipEdge[];

  readonly projection:
    CanonicalTreeViewerProjectionResult;
}): ReadonlyMap<string, string> => {
  const memberIds =
    new Set(
      members.map(
        member => member.id,
      ),
    );

  const memberById =
    new Map(
      members.map(
        member =>
          [member.id, member] as const,
      ),
    );

  const parentsByChild =
    new Map<string, string[]>();

  const childrenByParent =
    new Map<string, string[]>();

  for (
    const edge of edges
  ) {
    if (
      !isActiveParentEdge(edge) ||
      !memberIds.has(
        edge.sourceMemberId,
      ) ||
      !memberIds.has(
        edge.targetMemberId,
      )
    ) {
      continue;
    }

    const parents =
      parentsByChild.get(
        edge.targetMemberId,
      ) ?? [];

    parents.push(
      edge.sourceMemberId,
    );

    parentsByChild.set(
      edge.targetMemberId,
      parents,
    );

    const children =
      childrenByParent.get(
        edge.sourceMemberId,
      ) ?? [];

    children.push(
      edge.targetMemberId,
    );

    childrenByParent.set(
      edge.sourceMemberId,
      children,
    );
  }

  const paths =
    new Map<string, string>();

  paths.set(
    viewerId,
    'self',
  );

  const ancestorQueue:
    Array<{
      readonly memberId: string;
      readonly path: string;
    }> = [];

  const viewerParents =
    orderedParentIds(
      parentsByChild.get(
        viewerId,
      ) ?? [],
      projection,
      memberById,
    );

  viewerParents.forEach(
    (parentId, index) => {
      ancestorQueue.push({
        memberId:
          parentId,

        path:
          `ancestor.${index}`,
      });
    },
  );

  const visitedAncestors =
    new Set<string>();

  while (
    ancestorQueue.length > 0
  ) {
    const current =
      ancestorQueue.shift()!;

    if (
      visitedAncestors.has(
        current.memberId,
      )
    ) {
      continue;
    }

    visitedAncestors.add(
      current.memberId,
    );

    paths.set(
      current.memberId,
      current.path,
    );

    const parents =
      orderedParentIds(
        parentsByChild.get(
          current.memberId,
        ) ?? [],
        projection,
        memberById,
      );

    parents.forEach(
      (parentId, index) => {
        ancestorQueue.push({
          memberId:
            parentId,

          path:
            `${current.path}.${index}`,
        });
      },
    );
  }

  let changed = true;
  let pass = 0;

  while (
    changed &&
    pass++ < members.length + 2
  ) {
    changed = false;

    for (
      const member of members
    ) {
      if (
        paths.has(member.id)
      ) {
        continue;
      }

      const parentCandidates =
        orderedParentIds(
          parentsByChild.get(
            member.id,
          ) ?? [],
          projection,
          memberById,
        )
          .map(parentId => ({
            parentId,
            path:
              paths.get(parentId),
          }))
          .filter(
            (
              value,
            ): value is {
              parentId: string;
              path: string;
            } =>
              Boolean(value.path),
          )
          .sort(
            (left, right) =>
              left.path.localeCompare(
                right.path,
              ),
          );

      if (
        parentCandidates.length === 0
      ) {
        continue;
      }

      const selectedParent =
        parentCandidates[0];

      const siblings =
        orderedStableMemberIds(
          childrenByParent.get(
            selectedParent.parentId,
          ) ?? [],
        );

      const siblingIndex =
        Math.max(
          0,
          siblings.indexOf(
            member.id,
          ),
        );

      paths.set(
        member.id,
        `${selectedParent.path}.child.${siblingIndex}`,
      );

      changed = true;
    }
  }

  for (
    let passIndex = 0;
    passIndex < 4;
    passIndex += 1
  ) {
    let partnerChanged = false;

    for (
      const edge of edges
    ) {
      if (
        !isActivePartnerEdge(edge)
      ) {
        continue;
      }

      const sourcePath =
        paths.get(
          edge.sourceMemberId,
        );

      const targetPath =
        paths.get(
          edge.targetMemberId,
        );

      if (
        sourcePath &&
        !targetPath
      ) {
        paths.set(
          edge.targetMemberId,
          `${sourcePath}.partner`,
        );

        partnerChanged = true;
      } else if (
        targetPath &&
        !sourcePath
      ) {
        paths.set(
          edge.sourceMemberId,
          `${targetPath}.partner`,
        );

        partnerChanged = true;
      }
    }

    if (!partnerChanged) {
      break;
    }
  }

  return paths;
};

export const projectCanonicalTreeLayoutPaths = (
  input:
    CanonicalTreeLayoutPathCompatibilityInput,
): CanonicalTreeLayoutPathCompatibilityResult => {
  const members =
    [...input.members];

  const viewerId =
    input.viewerId ?? null;

  const legacyPathByMemberId =
    computeTreeBranchPaths(
      members,
      viewerId,
    );

  if (
    !viewerId ||
    !members.some(
      member =>
        member.id === viewerId,
    )
  ) {
    return Object.freeze({
      viewerId,

      layoutPathByMemberId:
        new Map(),

      legacyPathByMemberId:
        freezeMap(
          legacyPathByMemberId,
        ),

      projection:
        null,

      differences:
        Object.freeze([]),

      parityCount:
        0,

      canonicalAdditionCount:
        0,

      legacyOnlyCount:
        legacyPathByMemberId.size,

      mismatchCount:
        0,

      unresolvedEvidenceCount:
        1,

      conflictingEvidenceCount:
        0,

      readiness:
        'BLOCKED',
    });
  }

  const projection =
    projectCanonicalTreeForViewer({
      viewerId,
      members,
      edges:
        input.edges,
    });

  const layoutPathByMemberId =
    buildCanonicalLayoutPaths({
      members,
      viewerId,
      edges:
        input.edges,
      projection,
    });

  const memberIds =
    Array.from(
      new Set([
        ...legacyPathByMemberId.keys(),
        ...layoutPathByMemberId.keys(),
      ]),
    ).sort();

  const differences:
    CanonicalTreeLayoutPathDifference[] =
      memberIds.map(memberId => {
        const legacyPath =
          legacyPathByMemberId.get(
            memberId,
          ) ?? null;

        const canonicalLayoutPath =
          layoutPathByMemberId.get(
            memberId,
          ) ?? null;

        let classification:
          CanonicalTreeLayoutPathDifference['classification'];

        if (
          legacyPath ===
          canonicalLayoutPath
        ) {
          classification =
            'PARITY';
        } else if (
          legacyPath === null
        ) {
          classification =
            'CANONICAL-ADDITION';
        } else if (
          canonicalLayoutPath === null
        ) {
          classification =
            'LEGACY-ONLY';
        } else {
          classification =
            'PATH-MISMATCH';
        }

        return Object.freeze({
          memberId,
          classification,
          legacyPath,
          canonicalLayoutPath,
        });
      });

  const parityCount =
    differences.filter(
      difference =>
        difference.classification ===
        'PARITY',
    ).length;

  const canonicalAdditionCount =
    differences.filter(
      difference =>
        difference.classification ===
        'CANONICAL-ADDITION',
    ).length;

  const legacyOnlyCount =
    differences.filter(
      difference =>
        difference.classification ===
        'LEGACY-ONLY',
    ).length;

  const mismatchCount =
    differences.filter(
      difference =>
        difference.classification ===
        'PATH-MISMATCH',
    ).length;

  const unresolvedEvidenceCount =
    projection
      .unresolvedEvidence.length;

  const conflictingEvidenceCount =
    projection
      .conflictingEvidence.length;

  const blocked =
    unresolvedEvidenceCount > 0 ||
    conflictingEvidenceCount > 0 ||
    legacyOnlyCount > 0 ||
    mismatchCount > 0;

  const readiness =
    blocked
      ? 'BLOCKED'
      : canonicalAdditionCount > 0
        ? 'READY-WITH-CANONICAL-ADDITIONS'
        : 'READY';

  return Object.freeze({
    viewerId,

    layoutPathByMemberId:
      freezeMap(
        layoutPathByMemberId,
      ),

    legacyPathByMemberId:
      freezeMap(
        legacyPathByMemberId,
      ),

    projection,

    differences:
      Object.freeze(
        differences,
      ),

    parityCount,
    canonicalAdditionCount,
    legacyOnlyCount,
    mismatchCount,
    unresolvedEvidenceCount,
    conflictingEvidenceCount,
    readiness,
  });
};
